import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import DataTable from "../../../Components/ui/dataTable";
import { Button } from "../../../Components/ui/button";
import { Modal, ModalContent } from "../../../Components/ui/modal";
import { SkeletonTable } from "../../../Components/ui/skeleton";
import Pagination from "../../../Components/ui/pagination";
import Spinner from "../../../Assets/spinner";
import { parseMoney } from "../../../Lib/utils";
import {
  useGetAllZonePricesQuery,
  useGetAllZonesQuery,
  useCreateZonePriceMutation,
  useUpdateZonePriceMutation,
  useDeleteZonePriceMutation,
} from "../ZoneApi";
import ZonePriceForm from "./ZonePriceForm";
import { VEHICLE_PRICE_FIELDS, pairKey } from "../utils/vehicles";

const toPricesBody = (values) =>
  Object.fromEntries(
    VEHICLE_PRICE_FIELDS.map(({ field }) => [field, values[field] || 0]),
  );

const ZonePricingTab = () => {
  const [page, setPage] = useState(1);
  const [formModal, setFormModal] = useState(null); // { mode: "add" | "edit", zonePrice? }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading: loading } = useGetAllZonePricesQuery(
    { page, limit: 20 },
    { refetchOnMountOrArgChange: true },
  );
  const { data: zonesData } = useGetAllZonesQuery({ page: 1, limit: 100 });
  const { data: allZonePricesData } = useGetAllZonePricesQuery(
    { page: 1, limit: 1000 },
    { skip: formModal?.mode !== "add" },
  );

  const [createZonePrice, { isLoading: creating }] =
    useCreateZonePriceMutation();
  const [updateZonePrice, { isLoading: updating }] =
    useUpdateZonePriceMutation();
  const [deleteZonePrice, { isLoading: deleting }] =
    useDeleteZonePriceMutation();
  const saving = creating || updating;

  const zones = zonesData?.data || [];
  const zoneOptions = useMemo(
    () => zones.map((zone) => ({ value: zone.id, label: zone.name })),
    [zones],
  );
  const existingPairKeys = useMemo(
    () =>
      new Set(
        (allZonePricesData?.data || []).map((zp) =>
          pairKey(zp.originZoneId, zp.destinationZoneId),
        ),
      ),
    [allZonePricesData],
  );

  const resolveZoneName = (zonePrice, nestedKey) =>
    zonePrice?.[nestedKey]?.name || "N/A";

  const handleSubmit = async (values) => {
    const isAdd = formModal.mode === "add";

    try {
      const data = isAdd
        ? await createZonePrice({
            originZoneId: values.originZoneId,
            destinationZoneId: values.destinationZoneId,
            ...toPricesBody(values),
          }).unwrap()
        : await updateZonePrice({
            id: formModal.zonePrice.id,
            body: toPricesBody(values),
          }).unwrap();
      if (data?.statusCode < 300) {
        toast.success(isAdd ? "Zone price added" : "Zone price updated", {
          id: "zonePriceSaved",
        });
        setFormModal(null);
      }
    } catch (error) {
      toast.error("Could not save zone price", { id: "zonePriceSaved" });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const data = await deleteZonePrice(deleteTarget.id).unwrap();
      if (data?.statusCode < 300) {
        toast.success("Zone price deleted", { id: "zonePriceDelete" });
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error("Could not delete zone price", { id: "zonePriceDelete" });
    }
  };

  const columns = [
    {
      id: "origin",
      header: "Origin Zone",
      cell: ({ row }) => resolveZoneName(row.original, "originZone"),
    },
    {
      id: "destination",
      header: "Destination Zone",
      cell: ({ row }) => resolveZoneName(row.original, "destinationZone"),
    },
    ...VEHICLE_PRICE_FIELDS.map(({ field, label }) => ({
      id: field,
      header: label,
      cell: ({ row }) => {
        const price = row.original[field];
        return price > 0 ? (
          parseMoney(price)
        ) : (
          <span className='text-[#98A2B3]'>Not available</span>
        );
      },
    })),
    {
      id: "action",
      cell: ({ row }) => {
        const zonePrice = row.original;
        return (
          <div className='flex gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setFormModal({ mode: "edit", zonePrice })}
            >
              Edit
            </Button>
            <Button
              variant='failed'
              size='sm'
              onClick={() => setDeleteTarget(zonePrice)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className='flex items-start justify-between mb-4'>
        <div>
          <h3 className='text-lg font-bold text-primaryBlack'>Zone Pricing</h3>
          <p className='text-sm text-[#667499]'>
            Set the delivery price between any two zones for each vehicle.
            A route is only offered for a vehicle that has a price.
          </p>
        </div>
        <Button onClick={() => setFormModal({ mode: "add" })}>
          + Add Zone Price
        </Button>
      </div>

      <div className='table-scroll max-h-[70vh] overflow-auto max-w-full rounded-md border'>
        <div className='w-full'>
          {loading ? (
            <SkeletonTable />
          ) : (
            <DataTable columns={columns} data={data?.data} />
          )}
        </div>
      </div>

      {!loading && (
        <Pagination
          page={page}
          limit={data?.limit ?? 0}
          total={data?.total ?? 0}
          setPage={setPage}
        />
      )}

      <Modal
        open={Boolean(formModal)}
        onOpenChange={(open) => {
          if (!open) setFormModal(null);
        }}
      >
        <ModalContent
          name={formModal?.mode === "edit" ? "Edit Zone Price" : "Add Zone Price"}
        >
          {formModal && (
            <ZonePriceForm
              mode={formModal.mode}
              zoneOptions={zoneOptions}
              existingPairKeys={existingPairKeys}
              originZoneName={resolveZoneName(formModal.zonePrice, "originZone")}
              destinationZoneName={resolveZoneName(
                formModal.zonePrice,
                "destinationZone",
              )}
              initialValues={{
                originZoneId: formModal.zonePrice?.originZoneId || "",
                destinationZoneId: formModal.zonePrice?.destinationZoneId || "",
                motorcyclePrice: formModal.zonePrice?.motorcyclePrice ?? 0,
                bicyclePrice: formModal.zonePrice?.bicyclePrice ?? 0,
              }}
              submitting={saving}
              onSubmit={handleSubmit}
            />
          )}
        </ModalContent>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <ModalContent name='Delete Zone Price?'>
          <p className='text-sm text-primaryBlack my-4'>
            Are you sure you want to delete the price for{" "}
            <span className='font-semibold'>
              {resolveZoneName(deleteTarget, "originZone")}
              {" → "}
              {resolveZoneName(deleteTarget, "destinationZone")}
            </span>
            ? This cannot be undone.
          </p>
          <div className='flex justify-between items-center bg-white w-full'>
            <Button
              type='button'
              variant='outline'
              className='w-[45%]'
              onClick={() => setDeleteTarget(null)}
            >
              No
            </Button>
            <Button
              type='button'
              variant='failed'
              className='w-[45%]'
              disabled={deleting}
              onClick={handleConfirmDelete}
            >
              {deleting ? <Spinner /> : "Yes, Delete"}
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ZonePricingTab;
